import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { finalize, map, Observable } from 'rxjs';

// Services
import { MessageServiceBroker } from '../message.service';
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';

// Modèles
import { Partner } from 'src/app/@shared/models/partner/partner.model';
import { QueryOptions, QueryOptionsParam } from 'src/app/@shared/utilities/odata/query-params';

const CLASS_NAME = 'Partner';
const ENDPOINT = `/partners/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'addresses', path: 'addresses/', childClass: 'Address' },
    { key: 'contacts', path: 'contacts/', childClass: 'PartnerContact' },
    // Pièces jointes d'envoi propres au client, une collection par type de document (#1114)
    { key: 'credit-note-attachments', path: 'credit-note-attachments/', childClass: 'File' },
    { key: 'invoice-attachments', path: 'invoice-attachments/', childClass: 'File' },
    { key: 'quotation-attachments', path: 'quotation-attachments/', childClass: 'File' },
    { key: 'client-account-statement', path: 'client-account-statement/', childClass: 'AccountStatement', resultCardinality: 'one' },
    { key: 'vendor-account-statement', path: 'vendor-account-statement/', childClass: 'AccountStatement', resultCardinality: 'one' },
]
const ROUTES: RouteParam[] = [
    { key: 'export', path: 'export/' },
    { key: 'massDelete', path: 'mass-delete/' },
    { key: 'merge', path: '{id}/merge/' },
    { key: 'enrichContext', path: '{id}/enrich-context/' },
    { key: 'clientAccountStatement', path: '{id}/client-account-statement/$value/' },
    { key: 'vendorAccountStatement', path: '{id}/vendor-account-statement/$value/' },
]

@Injectable({
    providedIn: 'root'
})
export class PartnerService extends ServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    public downloadStatement$(partner: Partner, client: boolean = true, optionsIn?: QueryOptionsParam): Observable<HttpResponse<Blob>> {
        let route = client ? 'clientAccountStatement' : 'vendorAccountStatement';
        let url = this.routes[route].replace('{id}', partner.id.toString());
        let options = new QueryOptions(url, optionsIn);
        let queryUrl = options.toUrlString();
        if (optionsIn?.activityIndicator) {
            this._downloading$.next(true)
        }
        return this.httpClient.get(queryUrl, {
            observe: 'response',
            responseType: 'blob'
        }).pipe(
            finalize(() => {
                if (optionsIn?.activityIndicator) {
                    this._downloading$.next(false)
                }
            }),
            map(response => { return response })
        );
    }

    public enrichContext$(partner: Partner): Observable<Partner> {
        let url = this.routes.enrichContext.replace('{id}', partner.id.toString());
        return this._postAction$<Partner>(url);
    }

    public merge$(sourcePartner: Partner, targetPartner: Partner): Observable<any> {
        return this.update$(sourcePartner, {
            select: false,
            url: this.routes.merge.replace('{id}', targetPartner.id.toString())
        });
    }

    /**
     * Le contexte métier est écrit en arrière-plan par la recherche IA : un écran qui ne le
     * montre pas ne doit pas le renvoyer, la valeur lue à l'ouverture est périmée (#1267).
     */
    protected override cleanData(data: any, instance?: any): any {
        if (instance instanceof Partner && instance.omitBusinessContext) {
            delete data.businessContext;
        }
        return super.cleanData(data, instance);
    }
}
